import { useState } from 'react';
import { Button, Checkbox, Flex, Input, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatStock } from '@app/shared';
import { useBreakpoint, useElementHeight, useIsTouch } from '@/shared/lib/hooks';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { useWindowManager } from '@/shared/lib/windows';
import { CategoryThumb, GroupArchiveButton, useListCategoriesQuery, useSearchCategoriesQuery } from '@/entities/category';
import type { CategoryDto } from '@/entities/category';
import { useSelectableRow } from '@/shared/lib/hooks/useSelectableRow';
import { HotkeyHint } from '@/shared/lib/hotkeys';
import { HotkeyButton } from '@/shared/ui/hotkey';
import { useFocusTarget } from '@/shared/ui/inputs';
import { ResponsiveTable } from '@/shared/ui/ResponsiveTable';
import type { ResponsiveColumns } from '@/shared/ui/ResponsiveTable';

export interface CategoryGroupWindowProps {
  id: string;
  onClose: () => void;
}

/** Защитный кап на backend, не пагинация — категорий в одной группе практически его не достигает. */
const SEARCH_LIMIT = 1000;

const COLUMNS: ColumnsType<CategoryDto> = [
  { title: 'Фото', key: 'photo', width: 52, render: (_, c) => <CategoryThumb category={c} /> },
  { title: 'Название', dataIndex: 'name' },
  { title: 'N', dataIndex: 'pcsPerBox', width: 60, align: 'right' },
  { title: 'Опт. цена', dataIndex: 'wholesalePrice', width: 100, align: 'right', render: (v: string | null) => v ?? '—' },
  { title: 'Остаток', width: 140, render: (_, c) => (c.stock ? formatStock(c.stock) : '—') },
  { title: 'Новинка', dataIndex: 'isNew', width: 80, render: (v: boolean) => (v ? <Tag color="blue">новинка</Tag> : null) },
  { title: 'Акция', dataIndex: 'isPromo', width: 80, render: (v: boolean) => (v ? <Tag color="orange">акция</Tag> : null) },
  { title: 'Архив', dataIndex: 'isArchived', width: 70, render: (v: boolean) => (v ? <Tag>архив</Tag> : null) },
];

/** Телефон: карточка категории — фото и название сверху, метки справа, остальное строками «подпись — значение». */
const MOBILE_COLUMNS: ResponsiveColumns<CategoryDto> = [
  {
    title: 'Название',
    mobile: 'title',
    render: (_, c) => (
      <Flex align="center" gap={12} style={{ minWidth: 0 }}>
        <CategoryThumb category={c} size={48} />
        <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{c.name}</span>
      </Flex>
    ),
  },
  { title: 'Новинка', dataIndex: 'isNew', mobile: 'badge', render: (v: boolean) => (v ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>новинка</Tag> : null) },
  { title: 'Акция', dataIndex: 'isPromo', mobile: 'badge', render: (v: boolean) => (v ? <Tag color="orange" style={{ marginInlineEnd: 0 }}>акция</Tag> : null) },
  { title: 'Архив', dataIndex: 'isArchived', mobile: 'badge', render: (v: boolean) => (v ? <Tag style={{ marginInlineEnd: 0 }}>архив</Tag> : null) },
  { title: 'Штук в коробке', dataIndex: 'pcsPerBox', mobile: 'row' },
  { title: 'Опт. цена', dataIndex: 'wholesalePrice', mobile: 'row', render: (v: string | null) => v ?? '—' },
  { title: 'Остаток', mobile: 'row', render: (_, c) => (c.stock ? formatStock(c.stock) : '—') },
];

/** §2.1: плавающее окно группы — основные категории внутри корневой группы `id`, открывается
 *  двойным кликом по строке группы на `CategoriesPage`. Перемещается/растягивается как любое
 *  другое окно (`FloatingWindow`), можно держать несколько групп открытыми одновременно. */
export function CategoryGroupWindow({ id, onClose }: CategoryGroupWindowProps) {
  const windows = useWindowManager();
  const { isMobile } = useBreakpoint();
  const isTouch = useIsTouch();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  // Всегда с архивными: сама группа может лежать в архиве (заголовок и кнопки не должны пропадать),
  // а флаг «Показать архивные» скрывает только строки подкатегорий.
  const { data = [], isFetching } = useListCategoriesQuery({ includeArchived: true });
  const { attach: attachSearchFilter, focus: focusSearchFilter } = useFocusTarget<HTMLSpanElement>();
  // Тулбар закреплён вверху тела окна; шапка таблицы прилипает под ним (на телефоне таблица — карточки).
  const { ref: toolbarRef, height: toolbarHeight } = useElementHeight<HTMLDivElement>();

  const root = data.find((c) => c.id === id);
  const query = search.trim();
  // Поиск внутри группы — тоже на backend (`categories/search`, parentId: id), не по уже
  // загруженному `data`: без текста поиска список подкатегорий остаётся прежним (клиентский фильтр).
  const { data: matches, isFetching: isSearching } = useSearchCategoriesQuery(
    { q: query || undefined, limit: SEARCH_LIMIT, includeArchived, parentId: id },
    { skip: query === '' },
  );
  const mains =
    query === '' ? data.filter((c) => c.parentId === id && (includeArchived || !c.isArchived)) : matches ?? [];
  const selection = useSelectableRow(mains, {
    onOpen: (item) => windows.open({ kind: 'category', id: item.id }),
    onCreate: () => windows.open({ kind: 'categoryCreate', parentId: id }),
    // В архивную группу подкатегорию не добавить (§2.1) — сначала вернуть группу из архива.
    createDisabled: root?.isArchived ? 'Группа в архиве — сначала верните её из архива' : undefined,
    createLabel: 'Новая категория',
    autoFocus: false, // курсор ставит само окно: `data-autofocus` на таблице ниже
    onFocusFilters: focusSearchFilter,
    onResetFilters: () => setSearch(''),
    filtersEmpty: query === '',
  });

  // Телефон: лист — шапка группы, поиск, карточки категорий; «Новая категория» — в нижней панели.
  if (isMobile) {
    return (
      <FloatingWindow
        kind="categoryGroup"
        title={`Группа: ${root?.name ?? ''}`}
        onClose={onClose}
        footer={
          <HotkeyButton type="primary" size="large" block action={selection.actions.get('list.create')}>
            Новая категория
          </HotkeyButton>
        }
      >
        <Flex vertical gap={12}>
          {root ? (
            <Flex gap={12} align="center">
              <CategoryThumb category={root} size={56} previewable />
              <Flex vertical gap={8} align="flex-start" style={{ minWidth: 0, flex: 1 }}>
                <Typography.Text strong style={{ fontSize: 16, overflowWrap: 'anywhere' }}>
                  {root.name}
                </Typography.Text>
                <Space size={8} wrap>
                  <Button onClick={() => windows.open({ kind: 'category', id })}>Изменить группу</Button>
                  <GroupArchiveButton group={root} categories={data} size="middle" onArchived={onClose} />
                </Space>
              </Flex>
            </Flex>
          ) : null}

          <div className="sticky-head">
            <span ref={attachSearchFilter} style={{ display: 'block' }}>
              <Input
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                size="large"
                placeholder="Поиск по названию"
                style={{ WebkitAppearance: 'none', appearance: 'none' }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </span>
          </div>

          <Checkbox
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            style={{ minHeight: 44, display: 'flex', alignItems: 'center' }}
          >
            Показать архивные
          </Checkbox>

          <div {...selection.containerProps} data-autofocus>
            <ResponsiveTable<CategoryDto>
              rowKey="id"
              loading={isFetching || (query !== '' && isSearching)}
              dataSource={mains}
              columns={MOBILE_COLUMNS}
              pagination={false}
              rowClassName={(c) => (c.isArchived ? 'row-draft' : '')}
              onRow={selection.rowProps}
            />
          </div>
        </Flex>
      </FloatingWindow>
    );
  }

  return (
    <FloatingWindow kind="categoryGroup" title={`Группа: ${root?.name ?? ''}`} onClose={onClose}>
      <Flex vertical gap={8} style={{ minHeight: '100%' }}>
        {root ? (
          // Вход в карточку самой группы: название, фото (загрузка/замена/удаление), архив.
          <Flex gap={12} align="center">
            <CategoryThumb category={root} size={48} previewable />
            <Typography.Text strong>{root.name}</Typography.Text>
            <Button onClick={() => windows.open({ kind: 'category', id })}>Изменить группу</Button>
          </Flex>
        ) : null}

        <div className="sticky-head" ref={toolbarRef}>
          <Flex justify="space-between" align="center" gap={8} wrap>
            <Space>
              <HotkeyButton type="primary" action={selection.actions.get('list.create')}>
                Новая категория
              </HotkeyButton>
              <HotkeyButton action={selection.actions.get('list.open')}>Открыть</HotkeyButton>
              {root ? <GroupArchiveButton group={root} categories={data} size="middle" onArchived={onClose} /> : null}
              <span ref={attachSearchFilter}>
                <Input
                  placeholder="Поиск по названию"
                  allowClear
                  style={{ width: 200 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </span>
            </Space>
            <Checkbox checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}>
              Показать архивные
            </Checkbox>
          </Flex>
        </div>

        <div {...selection.containerProps} data-autofocus>
          <Table<CategoryDto>
            size="small"
            rowKey="id"
            loading={isFetching || (query !== '' && isSearching)}
            dataSource={mains}
            columns={COLUMNS}
            pagination={false}
            rowClassName={(c) => (c.isArchived ? 'row-draft' : '')}
            onRow={selection.rowProps}
            sticky={{ offsetHeader: toolbarHeight }}
          />
        </div>

        {isTouch ? null : <HotkeyHint bindings={selection.bindings} />}
      </Flex>
    </FloatingWindow>
  );
}
